/**
 * Theme. A `dark` class on <html>, persisted to localStorage['medtrack-theme'].
 *
 * index.html applies the stored value before first paint, so there is no flash
 * of the wrong theme. This module has to agree with that inline script exactly:
 * it reads the same key and treats a missing value as "follow the OS".
 *
 * `theme` is the RESOLVED theme — always 'light' or 'dark', always what is
 * actually on <html>. Chart code and anything else branching on appearance can
 * compare it directly without having to handle a third value. The user's
 * stored preference (which may be 'system') is exposed separately as
 * `preference`.
 *
 * No provider: a module-level store plus useSyncExternalStore means the shell
 * cannot forget to mount a context and leave a feature screen themeless.
 */

import { useCallback, useSyncExternalStore } from 'react';

export type ResolvedTheme = 'light' | 'dark';
export type ThemePreference = ResolvedTheme | 'system';

export const THEME_STORAGE_KEY = 'medtrack-theme';

const listeners = new Set<() => void>();

let preference: ThemePreference = 'system';
let resolved: ResolvedTheme = 'dark';
/** Cached snapshot object; useSyncExternalStore requires reference stability. */
let snapshot: ThemeSnapshot = { preference, theme: resolved };

interface ThemeSnapshot {
  preference: ThemePreference;
  theme: ResolvedTheme;
}

function prefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
    return 'system';
  } catch {
    // Private mode / storage disabled. Following the OS is the right fallback.
    return 'system';
  }
}

function writeStoredPreference(next: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    if (next === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* Nothing to do; the in-memory value still applies for this session. */
  }
}

function applyToDocument(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Keeps form controls, scrollbars and the iOS status bar in step.
  root.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#0B0D12' : '#FAFAFA');
}

function emit(): void {
  snapshot = { preference, theme: resolved };
  for (const listener of listeners) listener();
}

function recompute(): void {
  const next: ResolvedTheme = preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;
  const changed = next !== resolved;
  resolved = next;
  if (changed) applyToDocument(resolved);
  emit();
}

let bootstrapped = false;
let mediaQuery: MediaQueryList | null = null;

/**
 * Call once from main.tsx, before render. Idempotent.
 * Re-applies the class (index.html already did, but a stale service-worker
 * cache of index.html should not be the only thing keeping the theme right)
 * and subscribes to OS changes for users on 'system'.
 */
export function bootstrapTheme(): void {
  if (bootstrapped) return;
  bootstrapped = true;

  preference = readStoredPreference();
  resolved = preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;
  applyToDocument(resolved);
  snapshot = { preference, theme: resolved };

  if (typeof window !== 'undefined' && window.matchMedia) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      if (preference === 'system') recompute();
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
    } else if (typeof (mediaQuery as MediaQueryList).addListener === 'function') {
      // Safari < 14, which a self-hosted app on an old iPad may well be.
      (mediaQuery as MediaQueryList).addListener(onChange);
    }
  }

  // Another tab changed the theme: follow it, so a phone and a laptop on the
  // same account do not disagree within one browser profile.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      preference = readStoredPreference();
      recompute();
    });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ThemeSnapshot {
  return snapshot;
}

export function setTheme(next: ThemePreference): void {
  preference = next;
  writeStoredPreference(next);
  recompute();
}

export function getTheme(): ResolvedTheme {
  return resolved;
}

export interface UseThemeResult {
  /** The theme actually applied right now: 'light' or 'dark'. */
  theme: ResolvedTheme;
  /** What the user chose, which may be 'system'. */
  preference: ThemePreference;
  setTheme: (next: ThemePreference) => void;
  /** Flip between light and dark. Leaves 'system' for an explicit choice. */
  toggle: () => void;
}

export function useTheme(): UseThemeResult {
  bootstrapTheme();
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const toggle = useCallback(() => {
    setTheme(getTheme() === 'dark' ? 'light' : 'dark');
  }, []);

  return {
    theme: state.theme,
    preference: state.preference,
    setTheme,
    toggle,
  };
}

export default useTheme;
